import { Input } from "../../../../components/ui/input";
import { Check, Plus, Scale as ScaleIcon, Wifi, WifiOff } from "lucide-react";

/**
 * FulfillmentSettingsPanel — the seller-wide fulfillment defaults surface
 * (auto-buy toggle, parcel dims, preferred carrier, insurance rules,
 * label format, address validation, ship-from addresses / warehouses,
 * shipping profiles, devices/scale).
 *
 * Extracted from SellerDropship.jsx in Phase 2 refactor.
 * Scale device pairing + live readout moved here in Iter 186.
 */
export default function FulfillmentSettingsPanel({
  settings, patchSettings, saveSettings,
  settingsBusy, settingsDirty,
  addWarehouse, removeWarehouse, updateWarehouse, setDefaultWarehouse,
  setParserOpen,
  scale,
}) {
  return (
  <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="fulfillment-settings-panel">
    <div className="px-5 py-3 border-b border-border bg-secondary/40 flex items-center justify-between">
      <div className="text-[13px] font-medium">Fulfillment settings</div>
      {settingsDirty && (
        <button onClick={saveSettings} disabled={settingsBusy}
          data-testid="fulfillment-settings-save"
          className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-[11px] mono uppercase tracking-[0.14em] bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
          <Check className="w-3 h-3"/>{settingsBusy ? "Saving…" : "Save"}
        </button>
      )}
    </div>
    <div className="p-5 space-y-5">
      {/* Auto-buy toggle */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox"
          checked={settings.auto_buy_on_forward}
          onChange={e => patchSettings(s => { s.auto_buy_on_forward = e.target.checked; return s; })}
          data-testid="settings-auto-buy"
          className="mt-1 w-4 h-4 cursor-pointer accent-foreground"
        />
        <div className="flex-1">
          <div className="text-[13px] font-medium">Auto-buy Verqo label when order is forwarded</div>
          <div className="text-[11.5px] text-foreground/60 mt-0.5">
            Whenever a buyer forwards an order to you, we auto-purchase the cheapest Verqo-brokered label immediately. If the address is bad or the carrier declines, the order stays in <em>Ready to ship</em> for manual review. Every auto-purchased label is claim + insurance eligible.
          </div>
        </div>
      </label>

      {/* Defaults */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Default weight (lb)</label>
          <div className="relative">
            <Input type="number" min="0.1" step="0.1" max="150"
              value={settings.default_weight_lb}
              onChange={e => patchSettings(s => { s.default_weight_lb = Number(e.target.value || 1); return s; })}
              data-testid="settings-default-weight"
              className="h-10 mono pr-10"/>
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10.5px] mono text-foreground/55">lb</span>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {[
              ["L", "default_length_in"],
              ["W", "default_width_in"],
              ["H", "default_height_in"],
            ].map(([label, key]) => (
              <div key={key} className="relative">
                <Input type="number" min="0.1" step="0.5" max="108"
                  value={settings[key]}
                  onChange={e => patchSettings(s => { s[key] = Number(e.target.value || 1); return s; })}
                  data-testid={`settings-${key}`}
                  className="h-9 mono text-[12px] pr-8"/>
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] mono text-foreground/55">{label}·in</span>
              </div>
            ))}
          </div>
          <div className="text-[10.5px] text-foreground/55 mt-1">Default parcel dimensions used when the caller doesn&apos;t override.</div>

        </div>
        <div>
          <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Preferred carrier</label>
          <select value={settings.preferred_carrier}
            onChange={e => patchSettings(s => { s.preferred_carrier = e.target.value; return s; })}
            data-testid="settings-preferred-carrier"
            className="w-full h-10 rounded-lg border border-border bg-white px-3 text-[13px]">
            <option value="auto">Auto · cheapest recommended</option>
            <option value="usps">USPS</option>
            <option value="ups">UPS</option>
            <option value="fedex">FedEx</option>
            <option value="dhl">DHL Express</option>
          </select>
        </div>
      </div>

      {/* Insurance threshold + address validation. BYO carrier preference
          removed (Iter 217) — all Verqo-brokered labels apply the 10%
          markup so the option no longer makes sense. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Auto-insure ≥ ($)</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[11px] mono text-foreground/55">$</span>
            <Input type="number" min="0" step="10" max="100000"
              value={settings.auto_insure_min_order_value}
              onChange={e => patchSettings(s => { s.auto_insure_min_order_value = Number(e.target.value || 0); return s; })}
              data-testid="settings-auto-insure-min"
              className="h-10 mono pl-6"/>
          </div>
          <div className="text-[10.5px] text-foreground/55 mt-1">0 = never insure. Coverage matches order value.</div>
        </div>
        <div>
          <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Address validation</label>
          <select value={settings.address_validation_mode}
            onChange={e => patchSettings(s => { s.address_validation_mode = e.target.value; return s; })}
            data-testid="settings-address-validation"
            className="w-full h-10 rounded-lg border border-border bg-white px-3 text-[13px]">
            <option value="off">Off · trust buyer addresses</option>
            <option value="validate_only">Validate · warn on issues</option>
            <option value="enforce">Enforce · block on invalid</option>
          </select>
        </div>
      </div>


      {/* Package A · Print options (label format + auto-slip) */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Label format</label>
          <select value={settings.label_filetype}
            onChange={e => patchSettings(s => { s.label_filetype = e.target.value; return s; })}
            data-testid="settings-label-filetype"
            className="w-full h-10 rounded-lg border border-border bg-white px-2 text-[13px]">
            <option value="PDF">PDF · standard letter</option>
            <option value="PDF_4x6">PDF · 4×6 thermal</option>
            <option value="PNG">PNG · image</option>
            <option value="ZPLII">ZPL · Zebra printers</option>
          </select>
          <div className="text-[10.5px] text-foreground/55 mt-1">Pick 4×6 for thermal printers.</div>
        </div>
        <div className="col-span-2">
          <label className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 block mb-1">Pack slip with every label</label>
          <label className="flex items-center gap-2 h-10 px-3 rounded-lg border border-border bg-white cursor-pointer">
            <input type="checkbox"
              checked={settings.print_slip_with_label}
              onChange={e => patchSettings(s => { s.print_slip_with_label = e.target.checked; return s; })}
              data-testid="settings-print-slip-with-label"
              className="w-4 h-4 accent-foreground"/>
            <span className="text-[13px]">Auto-open combined label+pack-slip PDF after bulk-ship</span>
          </label>
          <div className="text-[10.5px] text-foreground/55 mt-1">One tab instead of two. Uses the same `/labels-with-slips` PDF the SCAN sheet uses.</div>
        </div>
      </div>

      {/* Package A · Shipping profiles CRUD */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] mono uppercase tracking-[0.16em] text-foreground/60">Shipping profiles</div>
          <button
            onClick={() => patchSettings(s => {
              const next = { ...s };
              next.shipping_profiles = [...(s.shipping_profiles || []), {
                id: null, name: `Profile ${s.shipping_profiles.length + 1}`,
                weight_lb: 1, length_in: 10, width_in: 8, height_in: 4,
                is_default: (s.shipping_profiles || []).length === 0,
              }];
              return next;
            })}
            data-testid="settings-add-profile"
            className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1">
            <Plus className="w-3 h-3"/>Add profile
          </button>
        </div>
        {settings.shipping_profiles.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-4 text-center text-[12px] text-foreground/55">
            No profiles yet. Add profiles like &ldquo;Small parcel&rdquo;, &ldquo;Poly mailer&rdquo;, &ldquo;Big box&rdquo; to speed up bulk-buy.
          </div>
        ) : (
          <div className="space-y-2">
            {settings.shipping_profiles.map((p, idx) => (
              <div key={p.id || idx}
                className={`rounded-xl border p-3 ${p.is_default ? "border-emerald-300 bg-emerald-50/30" : "border-border bg-white"}`}
                data-testid={`profile-${idx}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Input placeholder="Profile name" value={p.name || ""}
                    onChange={e => patchSettings(s => {
                      const next = { ...s, shipping_profiles: [...s.shipping_profiles] };
                      next.shipping_profiles[idx] = { ...p, name: e.target.value };
                      return next;
                    })}
                    data-testid={`profile-name-${idx}`}
                    className="h-9 flex-1 font-medium"/>
                  {p.is_default ? (
                    <span className="text-[10px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                      <Check className="w-3 h-3"/>Default
                    </span>
                  ) : (
                    <button
                      onClick={() => patchSettings(s => {
                        const next = { ...s, shipping_profiles: s.shipping_profiles.map((x, i) => ({ ...x, is_default: i === idx })) };
                        return next;
                      })}
                      data-testid={`profile-default-${idx}`}
                      className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 border border-border hover:bg-secondary">
                      Set default
                    </button>
                  )}
                  <button
                    onClick={() => patchSettings(s => {
                      const next = { ...s, shipping_profiles: s.shipping_profiles.filter((_, i) => i !== idx) };
                      if (next.shipping_profiles.length && !next.shipping_profiles.some(x => x.is_default)) {
                        next.shipping_profiles[0].is_default = true;
                      }
                      return next;
                    })}
                    data-testid={`profile-remove-${idx}`}
                    className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200">
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { key: "weight_lb", label: "Weight (lb)" },
                    { key: "length_in", label: "Length (in)" },
                    { key: "width_in",  label: "Width (in)" },
                    { key: "height_in", label: "Height (in)" },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-[9.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">{f.label}</label>
                      <Input type="number" step="0.1" min="0.1" value={p[f.key]}
                        onChange={e => patchSettings(s => {
                          const next = { ...s, shipping_profiles: [...s.shipping_profiles] };
                          next.shipping_profiles[idx] = { ...p, [f.key]: Number(e.target.value) || 0 };
                          return next;
                        })}
                        data-testid={`profile-${f.key}-${idx}`}
                        className="h-9 mono text-right"/>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ship-from address book */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] mono uppercase tracking-[0.16em] text-foreground/60">Ship-from warehouses</div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setParserOpen(true)} data-testid="parse-address-open"
              className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1">
              Parse address
            </button>
            <button onClick={addWarehouse} data-testid="settings-add-warehouse"
              className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-3 border border-border hover:bg-secondary inline-flex items-center gap-1">
              <Plus className="w-3 h-3"/>Add warehouse
            </button>
          </div>
        </div>
        {settings.ship_from_addresses.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-4 text-center text-[12px] text-foreground/55">
            No warehouses yet. Add at least one to enable Verqo shipping.
          </div>
        ) : (
          <div className="space-y-3">
            {settings.ship_from_addresses.map((a, idx) => (
              <div key={a.id || idx} className={`rounded-xl border p-3 ${a.is_default ? "border-emerald-300 bg-emerald-50/30" : "border-border bg-white"}`} data-testid={`warehouse-${idx}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Input placeholder="Warehouse name" value={a.name || ""}
                    onChange={e => updateWarehouse(idx, "name", e.target.value)}
                    className="h-9 flex-1 font-medium"/>
                  {a.is_default ? (
                    <span className="text-[10px] mono uppercase tracking-[0.14em] px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                      <Check className="w-3 h-3"/>Default
                    </span>
                  ) : (
                    <button onClick={() => setDefaultWarehouse(idx)}
                      className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 border border-border hover:bg-secondary">
                      Set default
                    </button>
                  )}
                  <button onClick={() => removeWarehouse(idx)}
                    className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200">
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  <Input placeholder="Street" value={a.street1 || ""}
                    onChange={e => updateWarehouse(idx, "street1", e.target.value)}
                    className="h-9 col-span-6"/>
                  <Input placeholder="Apt / Suite" value={a.street2 || ""}
                    onChange={e => updateWarehouse(idx, "street2", e.target.value)}
                    className="h-9 col-span-6"/>
                  <Input placeholder="City" value={a.city || ""}
                    onChange={e => updateWarehouse(idx, "city", e.target.value)}
                    className="h-9 col-span-3"/>
                  <Input placeholder="State" value={a.state || ""}
                    onChange={e => updateWarehouse(idx, "state", e.target.value)}
                    className="h-9 col-span-1"/>
                  <Input placeholder="ZIP" value={a.zip || ""}
                    onChange={e => updateWarehouse(idx, "zip", e.target.value)}
                    className="h-9 col-span-1"/>
                  <Input placeholder="Country" value={a.country || "US"}
                    onChange={e => updateWarehouse(idx, "country", e.target.value)}
                    className="h-9 col-span-1"/>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Devices · Shipping scale (WebHID) ─────────────────── */}
      {scale && (
        <div className="pt-5 border-t border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[13px] font-medium flex items-center gap-2">
                <ScaleIcon className="w-4 h-4"/>Shipping scale
              </div>
              <div className="text-[11.5px] text-foreground/60 mt-0.5">
                Pair a USB shipping scale (WebHID) to auto-fill package weight while packing. Chrome or Edge only.
              </div>
            </div>
            {scale.available ? (
              scale.paired ? (
                <button
                  onClick={() => scale.disconnect()}
                  data-testid="scale-disconnect"
                  className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-[11px] mono uppercase tracking-[0.14em] border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                >
                  <WifiOff className="w-3 h-3"/>Disconnect
                </button>
              ) : (
                <button
                  onClick={() => scale.pair()}
                  data-testid="scale-pair"
                  className="inline-flex items-center gap-1.5 rounded-full h-8 px-3 text-[11px] mono uppercase tracking-[0.14em] border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                >
                  <Wifi className="w-3 h-3"/>Pair scale
                </button>
              )
            ) : (
              <span className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50 border border-border rounded-full px-3 h-8 inline-flex items-center">
                Not supported in this browser
              </span>
            )}
          </div>

          {/* Live status card */}
          {scale.available && (
            <div className={`rounded-xl border p-4 flex items-center gap-4 ${
              scale.error         ? "border-rose-200 bg-rose-50"
              : scale.paired      ? "border-emerald-200 bg-emerald-50"
                                  : "border-border bg-secondary/40"
            }`}>
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                scale.error    ? "bg-rose-100 text-rose-700"
                : scale.paired ? "bg-emerald-100 text-emerald-700"
                               : "bg-white text-foreground/40"
              }`}>
                <ScaleIcon className="w-6 h-6"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">
                  {scale.error    ? "Error"
                  : scale.paired  ? "Paired · Live reading"
                                  : "Not paired"}
                </div>
                <div className="text-[15px] font-semibold text-foreground mt-0.5">
                  {scale.error ? (scale.error) :
                   scale.paired
                     ? (scale.weightLb != null
                         ? <>
                             {scale.weightLb.toFixed(2)} lb
                             {scale.stable && <span className="ml-2 text-emerald-700 text-[13px]">✓ stable</span>}
                           </>
                         : "Waiting for weight…")
                     : "Place package on scale after pairing"}
                </div>
                {scale.paired && scale.deviceName && (
                  <div className="text-[11px] text-foreground/50 mt-0.5 truncate">{scale.deviceName}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
  );
}
